/**
 * Passage annotation — shared external store.
 *
 * WHY THIS EXISTS: exactly the reason passageHighlightStore.ts exists. PassagePanel (desktop)
 * and PassageCard (narrow) are hidden from each other with CSS (`hidden lg:flex` /
 * `landscape:hidden lg:hidden`), NOT unmounted. Both are in the DOM on every viewport, so both
 * run the annotation hook at once. Two useState-backed hook instances would hold divergent mark
 * lists and race each other on the localStorage write.
 *
 * A module-level store consumed through useSyncExternalStore gives every consumer one snapshot
 * and one writer, at the cost of a single `sessionId` prop threaded to each host.
 *
 * All localStorage access is wrapped: Safari private mode throws on setItem, and quota can be
 * exhausted. Failure degrades to in-memory marks for the session — the passage stays readable
 * and markable either way, so there is nothing worth surfacing to a child mid-comprehension.
 * (This is the right trade ONLY because nothing is graded: a lost mark costs the child nothing.
 * It would not be acceptable if marks gated anything.)
 */

import {
  MAX_STORED_SESSIONS,
  ANNOTATION_STORAGE_KEY,
  clampMarks,
  parseAnnotationStore,
  pruneToRecent,
  type AnnotationMark,
  type AnnotationShape,
  type AnnotationStoreData,
  type PendingLink,
} from '@/lib/passageAnnotations';

export interface AnnotationSnapshot {
  marks: AnnotationMark[];
  armedShape: AnnotationShape | null;
  /**
   * The first end of a link the student has picked but not yet joined. Transient and never
   * persisted — a half-drawn arrow should not survive a reload.
   */
  pendingLink: PendingLink | null;
}

/**
 * Stable identity, returned for every inert (sessionId-less) consumer. useSyncExternalStore
 * compares snapshots by reference and would loop forever on a fresh object each call.
 */
const EMPTY_SNAPSHOT: AnnotationSnapshot = Object.freeze({
  marks: Object.freeze([]) as unknown as AnnotationMark[],
  armedShape: null,
  pendingLink: null,
});

const listeners = new Map<string, Set<() => void>>();
const snapshots = new Map<string, AnnotationSnapshot>();
const hydratedSessions = new Set<string>();

function readAll(): AnnotationStoreData {
  try {
    return parseAnnotationStore(localStorage.getItem(ANNOTATION_STORAGE_KEY));
  } catch {
    return {};
  }
}

function writeAll(data: AnnotationStoreData): void {
  try {
    localStorage.setItem(ANNOTATION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Private mode / quota exceeded. Marks stay in memory for this session.
  }
}

function notify(sessionId: string): void {
  listeners.get(sessionId)?.forEach((listener) => listener());
}

function setSnapshot(sessionId: string, next: AnnotationSnapshot): void {
  snapshots.set(sessionId, next);
  notify(sessionId);
}

export function subscribe(sessionId: string, listener: () => void): () => void {
  if (!sessionId) return () => {};

  let set = listeners.get(sessionId);
  if (!set) {
    set = new Set();
    listeners.set(sessionId, set);
  }
  set.add(listener);

  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(sessionId);
  };
}

export function getSnapshot(sessionId: string): AnnotationSnapshot {
  if (!sessionId) return EMPTY_SNAPSHOT;
  return snapshots.get(sessionId) ?? EMPTY_SNAPSHOT;
}

/**
 * Load persisted marks for this session. Idempotent — both mounted hosts call it.
 *
 * No-ops while the passage is still empty (React Query loading), because clamping against
 * length 0 would discard every stored mark and the next write would persist the loss.
 */
export function hydrate(sessionId: string, passageLength: number): void {
  if (!sessionId || passageLength === 0 || hydratedSessions.has(sessionId)) return;
  hydratedSessions.add(sessionId);

  const stored = readAll()[sessionId];
  const marks = clampMarks(stored?.marks ?? [], passageLength);
  if (marks.length === 0) return; // Leave EMPTY_SNAPSHOT in place; nothing to render.

  setSnapshot(sessionId, { ...getSnapshot(sessionId), marks });
}

export function setMarks(sessionId: string, marks: AnnotationMark[], passageLength: number): void {
  if (!sessionId) return;

  const clamped = clampMarks(marks, passageLength);
  setSnapshot(sessionId, { ...getSnapshot(sessionId), marks: clamped });

  const data = readAll();
  if (clamped.length === 0) {
    delete data[sessionId];
  } else {
    data[sessionId] = { marks: clamped, updatedAt: Date.now() };
  }
  writeAll(pruneToRecent(data, MAX_STORED_SESSIONS));
}

/**
 * Which tool is armed. Deliberately NOT persisted — every session starts with the tool down.
 *
 * Changing tools always drops a half-drawn link: an arrow waiting for its second end is only
 * meaningful while the link tool is still in the student's hand.
 */
export function setArmedShape(sessionId: string, armedShape: AnnotationShape | null): void {
  if (!sessionId) return;
  setSnapshot(sessionId, { ...getSnapshot(sessionId), armedShape, pendingLink: null });
}

/** The first end of a link, held until the student picks the second. Never persisted. */
export function setPendingLink(sessionId: string, pendingLink: PendingLink | null): void {
  if (!sessionId) return;
  setSnapshot(sessionId, { ...getSnapshot(sessionId), pendingLink });
}

/**
 * Module state outlives a Vitest file's localStorage.clear() in tests/setup.ts, so tests must
 * reset it explicitly between cases.
 */
export function __resetForTests(): void {
  listeners.clear();
  snapshots.clear();
  hydratedSessions.clear();
}
